import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserCog, Search, Ban, Trash2, Edit, Loader2, UserPlus, Globe, Clock, Eye, Shield } from 'lucide-react';
import { HelpButton } from '@/components/common/HelpButton';

// Highlight a substring match inside a value (case-insensitive) by wrapping
// matched chunks in a subtle yellow <mark>. Used by the Users table so
// partial matches are visually obvious to the admin.
const highlightMatch = (text, query) => {
  const t = text == null ? '' : String(text);
  const q = (query || '').trim();
  if (!q) return t;
  try {
    const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = t.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((chunk, i) =>
      chunk.toLowerCase() === q.toLowerCase()
        ? <mark key={`hl-${i}-${chunk}`} className="bg-yellow-200 text-slate-900 rounded px-0.5">{chunk}</mark>
        : <React.Fragment key={`fr-${i}-${chunk.slice(0, 8)}`}>{chunk}</React.Fragment>
    );
  } catch {
    return t;
  }
};

export const AdminUsersTab = ({
  users,
  usersLoading,
  userSearch,
  setUserSearch,
  loadUsers,
  handleUserAction,
  handleGhostLogin,
  onEditPermissions,
  editingUserSub,
  setEditingUserSub,
  newUserTier,
  setNewUserTier,
  handleChangeUserSubscription,
  savingSubscription,
  showAddUserDialog,
  setShowAddUserDialog,
  newUserForm,
  setNewUserForm,
  handleAddUser,
  addingUser,
  plans = [],
  roles = [],
  handleAssignRole,
}) => {
  // Format last activity time (e.g., "2 min ago", "3h ago", "5d ago")
  const formatLastActive = (timestamp) => {
    if (!timestamp) return { label: 'Never', online: false, fresh: false };
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 5) return { label: 'Online now', online: true, fresh: true };
    if (diffMins < 60) return { label: `${diffMins} min ago`, online: false, fresh: true };
    if (diffHours < 24) return { label: `${diffHours}h ago`, online: false, fresh: diffHours < 6 };
    if (diffDays < 7) return { label: `${diffDays}d ago`, online: false, fresh: false };
    return { label: date.toLocaleDateString(), online: false, fresh: false };
  };

  return (
    <>
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <UserCog className="w-5 h-5 text-blue-600" />User Management
            </CardTitle>
            <CardDescription className="text-slate-600">View and manage all platform users</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => setShowAddUserDialog(true)} 
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="add-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />Add User
            </Button>
            <HelpButton 
              title="User Management" 
              content={`<p>Manage all users:</p><ul><li><strong>Add User:</strong> Create new user accounts</li><li><strong>Search:</strong> Find by name or email</li><li><strong>Suspend:</strong> Temporarily disable access</li><li><strong>Delete:</strong> Permanently remove user and data</li></ul>`} 
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search by name or email..." 
                value={userSearch} 
                onChange={(e) => setUserSearch(e.target.value)} 
                onKeyDown={(e) => e.key === 'Enter' && loadUsers()} 
                className="pl-10 bg-white border-slate-300 text-slate-900 placeholder:text-slate-400" 
                data-testid="user-search-input" 
              />
            </div>
            <Button onClick={loadUsers} disabled={usersLoading} className="bg-blue-600 hover:bg-blue-700">
              {usersLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
            </Button>
          </div>
          
          <div className="border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">User</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Role</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Tier</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Last Active</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Location</th>
                  <th className="text-left p-3 text-sm text-slate-600 font-medium">Status</th>
                  <th className="text-right p-3 text-sm text-slate-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-slate-500">
                      {usersLoading ? 'Loading...' : 'Click Search to load users'}
                    </td>
                  </tr>
                ) : users.map((u) => (
                  <tr key={u.user_id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="p-3">
                      <p className="text-slate-900 font-medium" data-testid={`user-name-${u.user_id}`}>{highlightMatch(u.name, userSearch)}</p>
                      <p className="text-sm text-slate-500" data-testid={`user-email-${u.user_id}`}>{highlightMatch(u.email, userSearch)}</p>
                    </td>
                    <td className="p-3">
                      {(() => {
                        // Normalise legacy short role names ('user', 'system_admin',
                        // 'reseller', 'moderator') to the role_id so the <Select>
                        // controlled value always matches one of its <SelectItem>s.
                        // Otherwise the trigger renders blank on reload for the ~40%
                        // of users whose role was written by the pre-RBAC assign flow.
                        const normalisedRole = (() => {
                          if (!u.role) return 'role_user';
                          if (u.role.startsWith('role_')) return u.role;
                          const map = { user: 'role_user', system_admin: 'role_system_admin', moderator: 'role_moderator', reseller: 'role_reseller' };
                          return map[u.role] || u.role;
                        })();
                        const isAdmin = normalisedRole === 'role_system_admin';
                        return handleAssignRole && !isAdmin ? (
                          <Select
                            value={normalisedRole}
                            onValueChange={(v) => handleAssignRole(u.user_id, v)}
                          >
                            <SelectTrigger
                              className="w-[140px] h-8 bg-white border-slate-300 text-slate-700 capitalize"
                              data-testid={`role-select-${u.user_id}`}
                            >
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                              {roles.length === 0 ? (
                                <SelectItem value={normalisedRole}>{normalisedRole.replace('role_', '')}</SelectItem>
                              ) : (
                                roles.map((r) => (
                                  <SelectItem key={r.role_id} value={r.role_id} className="capitalize">
                                    {r.name}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge
                            variant="outline"
                            className={
                              isAdmin ? 'border-red-300 text-red-700 bg-red-50' :
                              normalisedRole === 'role_reseller' ? 'border-purple-300 text-purple-700 bg-purple-50' :
                              'border-slate-300 text-slate-600'
                            }
                          >
                            {normalisedRole.replace('role_', '') || 'user'}
                          </Badge>
                        );
                      })()}
                    </td>
                    <td className="p-3">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setEditingUserSub(u); setNewUserTier(u.tier || 'free'); }}
                        className="text-slate-700 hover:bg-blue-50 hover:text-blue-700 capitalize"
                        data-testid={`edit-tier-${u.user_id}`}
                      >
                        {u.tier || 'free'} <Edit className="w-3 h-3 ml-1" />
                      </Button>
                    </td>
                    <td className="p-3">
                      {(() => {
                        const a = formatLastActive(u.last_seen_at || u.last_login);
                        return (
                          <div className="flex items-center gap-1.5 text-sm" data-testid={`user-last-active-${u.user_id}`}>
                            {a.online ? (
                              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block animate-pulse" />
                            ) : (
                              <Clock className={`w-3.5 h-3.5 ${a.fresh ? 'text-emerald-500' : 'text-slate-400'}`} />
                            )}
                            <span className={a.online ? 'text-emerald-700 font-medium' : (a.fresh ? 'text-slate-700' : 'text-slate-500')}>
                              {a.label}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-3">
                      <div className="text-sm">
                        {u.last_login_country ? (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Globe className="w-3.5 h-3.5 text-slate-400" />
                            <span>{u.last_login_country}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                        {u.last_login_ip && (
                          <p className="text-xs text-slate-400 mt-0.5">{u.last_login_ip}</p>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {u.is_suspended ? (
                        <Badge variant="destructive">Suspended</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {u.role !== 'system_admin' && (
                        <div className="flex justify-end gap-2">
                          {handleGhostLogin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleGhostLogin(u)}
                              className="text-blue-600 hover:bg-blue-50"
                              title="Ghost login (impersonate)"
                              data-testid={`ghost-login-${u.user_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          {onEditPermissions && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onEditPermissions(u)}
                              className="text-purple-600 hover:bg-purple-50"
                              title="Edit permission overrides"
                              data-testid={`edit-perms-${u.user_id}`}
                            >
                              <Shield className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleUserAction(u.user_id, u.is_suspended ? 'unsuspend' : 'suspend')} 
                            className="text-amber-600 hover:bg-amber-50"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleUserAction(u.user_id, 'delete')} 
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Subscription Edit Modal */}
      <Dialog open={!!editingUserSub} onOpenChange={(open) => { if (!open) setEditingUserSub(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Change User Subscription</DialogTitle>
          </DialogHeader>
          {editingUserSub && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{editingUserSub.name}</p>
                <p className="text-sm text-slate-500">{editingUserSub.email}</p>
                <p className="text-sm text-slate-500 mt-1">
                  Current Tier: <span className="capitalize font-medium">{editingUserSub.tier || 'free'}</span>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-700">New Subscription Tier</Label>
                <Select value={newUserTier} onValueChange={setNewUserTier}>
                  <SelectTrigger className="bg-white border-slate-300">
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="free">Free</SelectItem>
                    {plans.filter(p => p.is_active).map((p) => (
                      <SelectItem key={p.plan_id} value={p.plan_id}>
                        {p.name}
                        {typeof p.monthly_price === 'number' && p.monthly_price > 0 && (
                          <span className="text-slate-500 ml-2">· ${p.monthly_price}/mo</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingUserSub(null)} className="border-slate-300">
                  Cancel
                </Button>
                <Button 
                  onClick={handleChangeUserSubscription} 
                  disabled={savingSubscription || newUserTier === editingUserSub.tier}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-subscription-btn"
                >
                  {savingSubscription ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add User Modal */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700">Full Name</Label>
              <Input 
                value={newUserForm?.name || ''} 
                onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                placeholder="Enter full name"
                className="bg-white border-slate-300 text-slate-900"
                data-testid="new-user-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-700">Email Address</Label>
              <Input 
                type="email"
                value={newUserForm?.email || ''} 
                onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                placeholder="Enter email address"
                className="bg-white border-slate-300 text-slate-900"
                data-testid="new-user-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-700">Password</Label>
              <Input 
                type="password"
                value={newUserForm?.password || ''} 
                onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                placeholder="Enter password"
                className="bg-white border-slate-300 text-slate-900"
                data-testid="new-user-password"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-700">Role</Label>
              <Select 
                value={newUserForm?.role || 'user'} 
                onValueChange={(v) => setNewUserForm({ ...newUserForm, role: v })}
              >
                <SelectTrigger className="bg-white border-slate-300">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {roles.length === 0 ? (
                    <>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="system_admin">System Admin</SelectItem>
                    </>
                  ) : (
                    roles.map((r) => (
                      <SelectItem key={r.role_id} value={r.role_id} className="capitalize">
                        {r.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-700">Subscription Tier</Label>
              <Select 
                value={newUserForm?.tier || 'free'} 
                onValueChange={(v) => setNewUserForm({ ...newUserForm, tier: v })}
              >
                <SelectTrigger className="bg-white border-slate-300">
                  <SelectValue placeholder="Select tier" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="free">Free</SelectItem>
                  {plans.filter(p => p.is_active).map((p) => (
                    <SelectItem key={p.plan_id} value={p.plan_id}>
                      {p.name}
                      {typeof p.monthly_price === 'number' && p.monthly_price > 0 && (
                        <span className="text-slate-500 ml-2">· ${p.monthly_price}/mo</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddUserDialog(false)} className="border-slate-300">
                Cancel
              </Button>
              <Button 
                onClick={handleAddUser} 
                disabled={addingUser || !newUserForm?.name || !newUserForm?.email || !newUserForm?.password}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="create-user-btn"
              >
                {addingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Create User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminUsersTab;
